import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { getWebSocketUrl } from '../utils/websocketUrl';

const REALTIME_BUFFER_HOURS = 6;

interface WebSocketStockData {
  stk_cd: string;
  stk_nm: string;
  stk_cls: string;
  cur_prc: string;
  flu_rt: string;
  now_trde_qty: string;
  sel_req: string;
  buy_req: string;
  pred_pre_sig: string;
  pred_pre: string;
  cntr_str: string;
  cnt: string;
  collected_at: string;
}

interface WebSocketMessage {
  type: string;
  data: WebSocketStockData[];
  count: number;
  timestamp: string;
}

export interface RealtimeRankingStockData {
  stock_code: string;
  stock_name: string;
  price: number;
  change_rate: number;
  volume: number;
  timestamp: string;
}

export interface TradeStockHistoryRecord {
  stock_code: string;
  trade_type: string;
  buy_rate: number;
  sell_rate: number;
  profit_rate: number;
  profit_amount: number;
  buy_price: number;
  sell_price: number;
  quantity: number;
  buy_tick_id: string;
  sell_tick_id: string;
  timestamp?: string;
}

export interface PortfolioPosition {
  stock_code: string;
  buy_price: number;
  current_price: number;
  quantity: number;
  unrealized_pnl: number;
  profit_rate: number;
  buy_time: string;
}

export interface PortfolioStatus {
  total_unrealized_pnl: number;
  total_invested: number;
  total_evaluated: number;
  roi_percent: number;
  positions: PortfolioPosition[];
  position_count: number;
}

interface RealtimeRankingFluctuationContextValue {
  data: RealtimeRankingStockData[];
  tradeStockHistoryRecords: TradeStockHistoryRecord[];
  portfolioStatus: PortfolioStatus | null;
  loading: boolean;
  error: string | null;
  isConnected: boolean;
}

const RealtimeRankingFluctuationContext = createContext<RealtimeRankingFluctuationContextValue | undefined>(
  undefined
);

const parseNumericValue = (value: string): number => {
  const cleaned = value.replace(/[+,-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseTimestampMs = (timestamp: string | number): number => {
  if (typeof timestamp === 'number') {
    // 이미 밀리초인 경우
    if (timestamp > 1e12) {
      return timestamp;
    }
    // 초 단위인 경우
    return timestamp * 1000;
  }
  
  // 문자열인 경우
  // 형식: "20260127024551.600000" (YYYYMMDDHHmmss.SSSSSS)
  if (/^\d{14}\.\d+$/.test(timestamp)) {
    const year = parseInt(timestamp.substring(0, 4));
    const month = parseInt(timestamp.substring(4, 6)) - 1; // 월은 0부터 시작
    const day = parseInt(timestamp.substring(6, 8));
    const hour = parseInt(timestamp.substring(8, 10));
    const minute = parseInt(timestamp.substring(10, 12));
    const second = parseInt(timestamp.substring(12, 14));
    const milliseconds = parseFloat(timestamp.substring(15));
    
    const date = new Date(Date.UTC(year, month, day, hour, minute, second, milliseconds));
    return date.getTime();
  }
  
  // ISO 형식 또는 기타 형식
  const parsed = new Date(timestamp);
  if (isNaN(parsed.getTime())) {
    console.warn('⚠️ 타임스탬프 파싱 실패:', timestamp);
    return Date.now();
  }
  return parsed.getTime();
};

export const RealtimeRankingFluctuationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [data, setData] = useState<RealtimeRankingStockData[]>([]);
  const [tradeStockHistoryRecords, setTradeStockHistoryRecords] = useState<TradeStockHistoryRecord[]>([]);
  const [portfolioStatus, setPortfolioStatus] = useState<PortfolioStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 실시간 등락률 차트: /stock 만 (대시보드는 거래일 고정 ka10027 차트 — WebSocket 미사용)
  const needsRealtimeChart = location.pathname === '/stock';

  const pruneOldData = useCallback((items: RealtimeRankingStockData[], nowMs: number) => {
    const cutoff = nowMs - REALTIME_BUFFER_HOURS * 60 * 60 * 1000;
    return items.filter(item => parseTimestampMs(item.timestamp) >= cutoff);
  }, []);

  const { isConnected, error: wsError } = useWebSocket({
    url: needsRealtimeChart ? getWebSocketUrl('stock', 'rt-ranking-fluctuation-daily') : '',
    reconnectInterval: 10000,
    // stock 서버가 늦게 기동되므로 무제한 재연결 시도
    reconnectLimit: needsRealtimeChart ? Infinity : 0,
    onMessage: (message: WebSocketMessage & { type?: string; data?: any; timestamp?: string }) => {
      console.log('📨 WebSocket 메시지 수신:', {
        type: message.type,
        hasData: !!message.data,
        dataLength: Array.isArray(message.data) ? message.data.length : 1,
        message
      });

      // trade_stock_history: mercury-stock에서 동일 WebSocket으로 브로드캐스트
      if (message.type === 'trade_stock_history' && message.data) {
        const msgData = message.data;
        const records: TradeStockHistoryRecord[] = Array.isArray(msgData)
          ? msgData
          : [{ ...(msgData as TradeStockHistoryRecord), timestamp: message.timestamp }];
        const withTimestamp = records.map((r) => ({
          ...r,
          timestamp: r.timestamp ?? message.timestamp,
        }));
        setTradeStockHistoryRecords((prev) => {
          const newRecords = [...withTimestamp, ...prev];
          return newRecords.slice(0, 100);
        });
        return;
      }

      // portfolio_status: 매수/매도 체결 직후 포지션 스냅샷
      if (message.type === 'portfolio_status' && message.data) {
        setPortfolioStatus(message.data as PortfolioStatus);
        return;
      }

      if (message.type !== 'kiwoom_daily_ranking' || !message.data) {
        return;
      }

      // 타임스탬프가 없으면 현재 시간 사용
      const messageTimestamp = message.timestamp || new Date().toISOString();
      const messageTimestampMs = parseTimestampMs(messageTimestamp);
      
      console.log('🕐 타임스탬프 파싱:', {
        original: message.timestamp,
        parsed: messageTimestamp,
        messageTimestampMs,
        isValid: !isNaN(messageTimestampMs),
      });

      setData(prevData => {
        const latestTimestamp = prevData.length > 0 ? parseTimestampMs(prevData[prevData.length - 1].timestamp) : 0;

        const convertedData = (message.data as WebSocketStockData[]).map((item: WebSocketStockData) => ({
          stock_code: item.stk_cd,
          stock_name: item.stk_nm,
          price: parseNumericValue(item.cur_prc),
          change_rate: parseNumericValue(item.flu_rt),
          volume: parseNumericValue(item.now_trde_qty),
          timestamp: messageTimestamp,
        }));

        console.log('🔄 데이터 변환:', {
          convertedDataLength: convertedData.length,
          prevDataLength: prevData.length,
          latestTimestamp,
          messageTimestamp,
          sampleConverted: convertedData.slice(0, 2),
        });

        const nowMs = Date.now();

        // 모든 타임스탬프의 데이터를 유지 (소수점 이하 6자리까지 포함)
        // 기존 데이터에서 같은 타임스탬프의 같은 종목 데이터만 제거하고 새 데이터 추가
        const otherTimestampData = prevData.filter(item => {
          const itemTimestampMs = parseTimestampMs(item.timestamp);
          // 같은 타임스탬프이고 같은 종목인 경우만 제거 (새 데이터로 교체)
          // convertedData의 모든 종목에 대해 체크
          return !convertedData.some((newItem: RealtimeRankingStockData) => 
            itemTimestampMs === messageTimestampMs && 
            item.stock_code === newItem.stock_code
          );
        });
        
        const combinedData = [...otherTimestampData, ...convertedData];
        const sortedData = combinedData.sort((a, b) => parseTimestampMs(a.timestamp) - parseTimestampMs(b.timestamp));

        // 타임스탬프와 종목 코드 조합으로 중복 제거 (같은 타임스탬프의 같은 종목은 최신 것만 유지)
        const uniqueData: RealtimeRankingStockData[] = [];
        const seenKeys = new Set<string>();
        // 역순으로 순회하여 같은 키의 경우 나중 것(최신)을 유지
        for (let i = sortedData.length - 1; i >= 0; i--) {
          const item = sortedData[i];
          const key = `${item.timestamp}_${item.stock_code}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueData.unshift(item); // 앞에 추가하여 순서 유지
          }
        }

        const prunedData = pruneOldData(uniqueData, nowMs);
        console.log('✅ 최종 데이터 (모든 타임스탬프 유지):', {
          prunedDataLength: prunedData.length,
          messageTimestampMs,
          latestTimestamp,
          prevDataLength: prevData.length,
          sampleData: prunedData.slice(0, 2),
        });
        return prunedData;
      });
    },
    onOpen: () => {
      setLoading(false);
      setError(null);
    },
    onError: () => {
      setLoading(false);
      setError('WebSocket 오류가 발생했습니다.');
    },
    onClose: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (wsError) {
      setError(wsError);
    }
  }, [wsError]);

  const value = useMemo(
    () => ({
      data: needsRealtimeChart ? data : [],
      tradeStockHistoryRecords: needsRealtimeChart ? tradeStockHistoryRecords : [],
      portfolioStatus: needsRealtimeChart ? portfolioStatus : null,
      loading: needsRealtimeChart ? loading : false,
      error: needsRealtimeChart ? error : null,
      isConnected: needsRealtimeChart ? isConnected : false,
    }),
    [data, tradeStockHistoryRecords, portfolioStatus, loading, error, isConnected, needsRealtimeChart]
  );

  return (
    <RealtimeRankingFluctuationContext.Provider value={value}>
      {children}
    </RealtimeRankingFluctuationContext.Provider>
  );
};

export const useRealtimeRankingFluctuationDaily = (): RealtimeRankingFluctuationContextValue => {
  const context = useContext(RealtimeRankingFluctuationContext);
  if (!context) {
    throw new Error('useRealtimeRankingFluctuationDaily must be used within RealtimeRankingFluctuationProvider');
  }
  return context;
};
